import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform, Animated, useWindowDimensions } from 'react-native';
import { colores } from '../../../../styles/variablesCss';
import RangeSlider from './RangeSlider';



const Filtros = ({ 
  productos, 
  onFiltersChanged, 
  categorias = [],
  marcas = [],
  closeFilters, // opcional, lo provee el padre en móvil
}) => {
  const { width } = useWindowDimensions();
  const isSmallScreen = width <= 600; // breakpoint aproximado para móviles

  const productosSignature = useMemo(() => {
    try {
      if (!productos || !Array.isArray(productos) || productos.length === 0) return '0';
      const first = productos[0]?.plu;
      const last = productos[productos.length - 1]?.plu;
      return `${productos.length}:${String(first)}:${String(last)}`;
    } catch (_) {
      return '0';
    }
  }, [productos]);
  // Función auxiliar para obtener el precio de un producto como número
  const obtenerPrecioNumerico = (producto) => {
    if (!producto || typeof producto !== 'object') return null;
    
    // Intentar extraer el precio como número
    const precioStr = producto.precio;
    if (precioStr === undefined || precioStr === null) return null;
    
    // Convertir a número eliminando cualquier formato
    const precioNumerico = parseFloat(String(precioStr).replace(/[^0-9,.]/g, '').replace(',', '.'));
    return !isNaN(precioNumerico) && precioNumerico > 0 ? precioNumerico : null;
  };

  // Calcular los precios mínimos y máximos de manera robusta
  const calcularPreciosMinMax = () => {
    
    // Valores por defecto
    const valoresPorDefecto = { min: 0, max: 5000, precios: [] };
    
    // Si no hay productos, usar valores por defecto silenciosamente (sin warnings)
    if (!productos || !Array.isArray(productos) || productos.length === 0) {
      // Solo mostramos el warning en desarrollo, no en producción
      if (process.env.NODE_ENV === 'development') {
      }
      return valoresPorDefecto;
    }
    
    // Extraer precios válidos
    const preciosValidos = [];
    productos.forEach((producto, index) => {
      const precio = obtenerPrecioNumerico(producto);
      if (precio !== null) {
        preciosValidos.push(precio);
      } else {
      }
    });
    
    // Verificar que hay precios válidos
    if (preciosValidos.length === 0) {
      console.warn('No se encontraron precios válidos en los productos');
      return valoresPorDefecto;
    }
    
    // Calcular mínimo y máximo
    const min = Math.min(...preciosValidos);
    const max = Math.max(...preciosValidos);
    
    // Verificar que los valores son válidos
    if (isNaN(min) || isNaN(max) || min === Infinity || max === -Infinity) {
      console.error('Error en el cálculo de precios min/max:', { min, max });
      return valoresPorDefecto;
    }
    
    return { min, max, precios: preciosValidos };
  };
  
  // IMPORTANTE: Estados con valores iniciales por defecto
  // Inicializar con valores temporales
  const [preciosAnalizados, setPreciosAnalizados] = useState({
    min: 0,
    max: 10000,
    precios: []
  });
  
  // Estado inicial de rangos temporales
  const [tempPriceRange, setTempPriceRange] = useState({ 
    min: 0, 
    max: 10000 
  });

  // Estados para los filtros (MOVIDOS ARRIBA antes del useEffect)
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedPriceRange, setSelectedPriceRange] = useState({ min: '', max: '' });
  
  // CRÍTICO: Calcular y establecer los rangos de precios iniciales
  // Este efecto se ejecuta una sola vez al principio y cada vez que cambian los productos
  useEffect(() => {
    if (productos && Array.isArray(productos)) {
      
      // Calcular nuevos rangos
      const nuevosPrecios = calcularPreciosMinMax();
      
      // Actualizar el estado con los nuevos rangos
      setPreciosAnalizados((prev) => {
        const prevMin = prev?.min;
        const prevMax = prev?.max;
        const prevLen = Array.isArray(prev?.precios) ? prev.precios.length : 0;
        const nextLen = Array.isArray(nuevosPrecios?.precios) ? nuevosPrecios.precios.length : 0;
        if (prevMin === nuevosPrecios.min && prevMax === nuevosPrecios.max && prevLen === nextLen) {
          return prev;
        }
        return nuevosPrecios;
      });
      
      // También actualizar el rango temporal con estos valores
      setTempPriceRange((prev) => {
        if (prev?.min === nuevosPrecios.min && prev?.max === nuevosPrecios.max) return prev;
        return {
          min: nuevosPrecios.min,
          max: nuevosPrecios.max
        };
      });
      
      // SOLO resetear los rangos cuando es la primera carga o cuando realmente cambia el array de productos
      // utilizando una condición para evitar actualizaciones innecesarias
      if (productos.length > 0) {
        // Usamos el valor previo para evitar actualizaciones cíclicas
        setSelectedPriceRange((prev) => {
          const shouldReset = prev?.min !== '' || prev?.max !== '';
          return shouldReset ? { min: '', max: '' } : prev;
        });
        setPriceRange((prev) => {
          const shouldReset = prev?.min !== '' || prev?.max !== '';
          return shouldReset ? { min: '', max: '' } : prev;
        });
      }
      
    } else {
    
    }
  }, [productosSignature]); // Solo se ejecuta cuando cambia realmente el set de productos
  

  // Estados para las categorías y marcas seleccionadas
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedMarcas, setSelectedMarcas] = useState([]);
  const [showCategoriasOptions, setShowCategoriasOptions] = useState(false);
  const [showSubcategoriasOptions, setShowSubcategoriasOptions] = useState(false);
  const [showMarcasOptions, setShowMarcasOptions] = useState(false);
  const [showContenidoOptions, setShowContenidoOptions] = useState(false);
  const [selectedUnidadesMedida, setSelectedUnidadesMedida] = useState([]);

  const lastEmittedRef = useRef([]);
  const productosRef = useRef(productos);

  useEffect(() => {
    // Al cambiar el set de productos, reseteamos la cache de emisión y actualizamos ref
    lastEmittedRef.current = [];
    productosRef.current = productos;
  }, [productosSignature, productos]);

  // Aplicar filtros a los productos
  // Actualizar tempPriceRange cuando cambie maxPossiblePrice o selectedPriceRange
  // Actualizar el estado temporal del slider cuando cambia el rango seleccionado
  useEffect(() => {
    // Si hay un rango seleccionado, lo aplicamos (pero dentro de los límites disponibles)
    // Añadimos comprobación para evitar cambios innecesarios que causan ciclos infinitos
    if ((selectedPriceRange.min !== '' || selectedPriceRange.max !== '') && 
        preciosAnalizados && preciosAnalizados.min !== undefined && preciosAnalizados.max !== undefined) {
      
      // Valores actuales para comparar y evitar actualizaciones innecesarias
      const currentMin = tempPriceRange.min;
      const currentMax = tempPriceRange.max;
      
      let newMin = preciosAnalizados.min;
      let newMax = preciosAnalizados.max;
      
      if (selectedPriceRange.min !== '') {
        const selectedMin = parseFloat(selectedPriceRange.min);
        if (!isNaN(selectedMin)) {
          newMin = Math.max(preciosAnalizados.min, Math.min(preciosAnalizados.max, selectedMin));
        }
      }
      
      if (selectedPriceRange.max !== '') {
        const selectedMax = parseFloat(selectedPriceRange.max);
        if (!isNaN(selectedMax)) {
          newMax = Math.min(preciosAnalizados.max, Math.max(preciosAnalizados.min, selectedMax));
        }
      }
      
      // Asegurar que min <= max
      if (newMin > newMax) {
        newMin = preciosAnalizados.min;
        newMax = preciosAnalizados.max;
      }
      
      // Solo actualizar si los valores han cambiado realmente
      if (newMin !== currentMin || newMax !== currentMax) {
        setTempPriceRange({
          min: newMin,
          max: newMax
        });
      }
    }
  }, [selectedPriceRange, preciosAnalizados]);

  useEffect(() => {
    const currentProductos = productosRef.current;
    if (!currentProductos) return;

    let result = [...currentProductos];

    // Aplicar filtro de rango de precios si están definidos
    // Aplicar todos los filtros de una sola vez para evitar redundancia
    result = result.filter(producto => {
      // Filtro por rango de precios
      const precio = parseFloat(producto.precio || '0');
      const min = selectedPriceRange.min === '' ? 0 : parseFloat(selectedPriceRange.min);
      const max = selectedPriceRange.max === '' ? Infinity : parseFloat(selectedPriceRange.max);
      const cumpleRangoPrecio = precio >= min && precio <= max;
      
      // Filtro por categorías
      const cumpleCategoria = selectedCategories.length === 0 || 
        (producto.desCat && selectedCategories.includes(producto.desCat));
      
      // Filtro por subcategorías
      const cumpleSubcategoria = selectedSubcategories.length === 0 || 
        (producto.desScat && selectedSubcategories.includes(producto.desScat));
      
      // Filtro por marcas
      const cumpleMarca = selectedMarcas.length === 0 || 
        (producto.marca && selectedMarcas.includes(producto.marca));
      
      // Filtro por unidad de medida (combinación de medida y umedida)
      const combinacionMedida = producto.medida && producto.umedida ? 
        `${producto.medida} ${producto.umedida}` : '';
      const cumpleUnidadMedida = selectedUnidadesMedida.length === 0 || 
        (combinacionMedida && selectedUnidadesMedida.includes(combinacionMedida));
      
      // Devolver true solo si cumple con todos los filtros
      return cumpleRangoPrecio && cumpleCategoria && cumpleSubcategoria && cumpleMarca && cumpleUnidadMedida;
    });

    // Enviar los productos filtrados al componente padre
    if (typeof onFiltersChanged === 'function') {
      const prev = lastEmittedRef.current;
      let same = Array.isArray(prev) && prev.length === result.length;
      if (same) {
        for (let i = 0; i < result.length; i++) {
          if (String(prev[i]?.plu) !== String(result[i]?.plu)) {
            same = false;
            break;
          }
        }
      }

      if (!same) {
        lastEmittedRef.current = result;
        onFiltersChanged(result);
      }
    }
  }, [productosSignature, selectedPriceRange, selectedCategories, selectedSubcategories, selectedMarcas, selectedUnidadesMedida]);

  // Restablecer todos los filtros a sus valores predeterminados
  const handleResetFilters = () => {
    
    // Verificar que tenemos límites válidos
    if (isNaN(preciosAnalizados.min) || isNaN(preciosAnalizados.max) || 
        preciosAnalizados.min === undefined || preciosAnalizados.max === undefined) {
      console.warn('Límites de precios no válidos al resetear:', preciosAnalizados);
      return;
    }
    
    // Restablecer tempPriceRange a los límites calculados de los productos
    setTempPriceRange({ 
      min: preciosAnalizados.min, 
      max: preciosAnalizados.max 
    });
    
    // Limpiar otros filtros
    setSelectedPriceRange({ min: '', max: '' });
    setPriceRange({ min: '', max: '' });
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedMarcas([]);
    setSelectedUnidadesMedida([]);
    // En pantallas pequeñas, ocultar el panel si el padre provee la función
    if (isSmallScreen && typeof closeFilters === 'function') {
      closeFilters();
    }
  };

  const handleApplyPriceFilter = () => {
    
    // Validar y asegurar que los valores son números válidos
    let validMin = typeof tempPriceRange.min === 'number' ? tempPriceRange.min : preciosAnalizados.min;
    let validMax = typeof tempPriceRange.max === 'number' ? tempPriceRange.max : preciosAnalizados.max;
    
    // Asegurar que los valores están dentro de los límites permitidos
    validMin = Math.max(preciosAnalizados.min, Math.min(validMin, validMax - 1));
    validMax = Math.min(preciosAnalizados.max, Math.max(validMax, validMin + 1));
        
    // Actualizar el rango de precios seleccionado con los valores validados
    setSelectedPriceRange({
      min: validMin,
      max: validMax
    });
    
    // Formato para la visualización
    setPriceRange({
      min: validMin,
      max: validMax
    });
    
    // Actualizar también el estado temporal para mantener consistencia
    setTempPriceRange({
      min: validMin,
      max: validMax
    });
    // En pantallas pequeñas, ocultar el panel si el padre provee la función
    if (isSmallScreen && typeof closeFilters === 'function') {
      closeFilters();
    }
  };

  const handleCategoryToggle = (categoria) => {
    if (categoria === 'TODAS') {
      setSelectedCategories([]);
    } else if (selectedCategories.includes(categoria)) {
      setSelectedCategories(selectedCategories.filter(cat => cat !== categoria));
    } else {
      setSelectedCategories([...selectedCategories, categoria]);
    }
  };

  // Maneja la selección/deselección de una marca
  const handleMarcaToggle = (marca) => {
    if (marca === 'TODAS') {
      setSelectedMarcas([]);
    } else if (selectedMarcas.includes(marca)) {
      setSelectedMarcas(selectedMarcas.filter(m => m !== marca));
    } else {
      setSelectedMarcas([...selectedMarcas, marca]);
    }
  };


  // Extraer categorías únicas de los productos para el filtro
  const getCategorias = () => {
    if (!productos) return [];
    
    const uniqueCategories = [];
    productos.forEach(producto => {
      if (producto && producto.desCat && !uniqueCategories.includes(producto.desCat)) {
        uniqueCategories.push(producto.desCat);
      }
    });
    
    return uniqueCategories.sort();
  };

  // Extraer subcategorías únicas de los productos para el filtro
  const getSubcategorias = () => {
    if (!productos) return [];
    
    // Si hay categorías seleccionadas, solo mostrar subcategorías de esas categorías
    let filteredProductos = productos;
    if (selectedCategories.length > 0) {
      filteredProductos = productos.filter(producto => 
        selectedCategories.includes(producto.desCat)
      );
    }
    
    const uniqueSubcategories = [];
    filteredProductos.forEach(producto => {
      if (producto && producto.desScat && !uniqueSubcategories.includes(producto.desScat)) {
        uniqueSubcategories.push(producto.desScat);
      }
    });
    
    return uniqueSubcategories.sort();
  };

  const Chevron = ({ open }) => {
    const rotate = open ? '135deg' : '-45deg';
    return (
      <View style={styles.chevronWrapper}>
        <View style={[styles.chevron, { transform: [{ rotate }] }]} />
      </View>
    );
  };

  // Toggle para seleccionar/deseleccionar una categoría
  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      const newCategories = prev.includes(category) 
        ? prev.filter(cat => cat !== category)
        : [...prev, category];
      
      // Al cambiar las categorías, resetear las subcategorías seleccionadas
      // para evitar subcategorías que ya no aplican
      if (prev.length !== newCategories.length) {
        setSelectedSubcategories([]);
      }
      
      return newCategories;
    });
  };
  
  // Toggle para seleccionar/deseleccionar una subcategoría
  const toggleSubcategory = (subcategory) => {
    if (subcategory === 'TODAS') {
      setSelectedSubcategories([]);
    } else {
      setSelectedSubcategories(prev => {
        if (prev.includes(subcategory)) {
          return prev.filter(item => item !== subcategory);
        } else {
          return [...prev, subcategory];
        }
      });
    }
  };

  // Extraer unidades de medida únicas de los productos para el filtro
  const getUnidadesMedida = () => {
    if (!productos) return [];
    
    const uniqueUnidades = [];
    const uniqueCombinaciones = [];
    
    productos.forEach(producto => {
      if (producto && producto.medida && producto.umedida) {
        // Crear la combinación de medida + unidad (ej: "500 GR.")
        const combinacion = `${producto.medida} ${producto.umedida}`;
        
        // Verificar que no exista ya en nuestro array de combinaciones únicas
        if (!uniqueCombinaciones.includes(combinacion)) {
          uniqueCombinaciones.push(combinacion);
          uniqueUnidades.push({
            combinacion,
            medida: producto.medida,
            umedida: producto.umedida
          });
        }
      }
    });
    
    // Ordenar por unidad de medida y luego por valor numérico
    return uniqueUnidades.sort((a, b) => {
      if (a.umedida !== b.umedida) {
        return a.umedida.localeCompare(b.umedida);
      }
      return a.medida - b.medida;
    });
  };

  // Maneja la selección/deselección de una unidad de medida
  const handleUnidadMedidaToggle = (combinacion) => {
    if (combinacion === 'TODAS') {
      setSelectedUnidadesMedida([]);
    } else if (selectedUnidadesMedida.includes(combinacion)) {
      setSelectedUnidadesMedida(selectedUnidadesMedida.filter(u => u !== combinacion));
    } else {
      setSelectedUnidadesMedida([...selectedUnidadesMedida, combinacion]);
    }
  };

  // Render reutilizable para los botones de acción
  const renderActionButtons = () => (
    <View style={[styles.buttonsContainer, isSmallScreen && styles.buttonsContainerTop]}>
      <TouchableOpacity 
        style={styles.applyPriceButton}
        onPress={handleApplyPriceFilter}
      >
        <Text style={styles.applyPriceButtonText}>Aplicar</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={styles.clearFiltersButton}
        onPress={handleResetFilters}
      >
        <Text style={styles.clearFiltersButtonText}>Limpiar Filtros</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Panel lateral de filtros */}
      <View style={styles.filtersPanelContainer}>
        <Text style={styles.filtersPanelTitle}>FILTROS</Text>
        {/* En pantallas pequeñas, mostrar botones debajo del título */}
        {isSmallScreen && renderActionButtons()}

        {/* Categorías */}
        <View style={styles.filterColumn}>
          <View style={styles.filterTitleRow}>
            <TouchableOpacity 
              style={styles.filterTitleButton}
              onPress={() => setShowCategoriasOptions(!showCategoriasOptions)}
            >
              <Text style={styles.filterCategoryTitle}>Categorías</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.filterExpandButton}
              onPress={() => setShowCategoriasOptions(!showCategoriasOptions)}
            >
              <Chevron open={showCategoriasOptions} />
            </TouchableOpacity>
          </View>

          {showCategoriasOptions && (
            <View style={styles.filterOptionsContainer}>
              <TouchableOpacity
                style={[styles.categoryItem, selectedCategories.length === 0 && styles.activeCategoryItem]}
                onPress={() => handleCategoryToggle('TODAS')}
              >
                <Text style={[styles.categoryText, selectedCategories.length === 0 && styles.activeCategoryText]}>TODAS</Text>
              </TouchableOpacity>

              {getCategorias().map(categoria => (
                <TouchableOpacity
                  key={categoria}
                  style={[styles.categoryItem, selectedCategories.includes(categoria) && styles.activeCategoryItem]}
                  onPress={() => toggleCategory(categoria)}
                >
                  <Text style={[styles.categoryText, selectedCategories.includes(categoria) && styles.activeCategoryText]}>
                    {categoria}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Subcategorías */}
        <View style={styles.filterColumn}>
          <View style={styles.filterTitleRow}>
            <TouchableOpacity 
              style={styles.filterTitleButton}
              onPress={() => setShowSubcategoriasOptions(!showSubcategoriasOptions)}
            >
              <Text style={styles.filterCategoryTitle}>Subcategorías</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.filterExpandButton}
              onPress={() => setShowSubcategoriasOptions(!showSubcategoriasOptions)}
            >
              <Chevron open={showSubcategoriasOptions} />
            </TouchableOpacity>
          </View>

          {showSubcategoriasOptions && (
            <View style={styles.filterOptionsContainer}>
              <TouchableOpacity
                style={[styles.categoryItem, selectedSubcategories.length === 0 && styles.activeCategoryItem]}
                onPress={() => toggleSubcategory('TODAS')}
              >
                <Text style={[styles.categoryText, selectedSubcategories.length === 0 && styles.activeCategoryText]}>TODAS</Text>
              </TouchableOpacity>
              
              {getSubcategorias().map(subcategoria => (
                <TouchableOpacity
                  key={subcategoria}
                  style={[styles.categoryItem, selectedSubcategories.includes(subcategoria) && styles.activeCategoryItem]}
                  onPress={() => toggleSubcategory(subcategoria)}
                >
                  <Text style={[styles.categoryText, selectedSubcategories.includes(subcategoria) && styles.activeCategoryText]}>
                    {subcategoria}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Marca */}
        <View style={styles.filterColumn}>
          <View style={styles.filterTitleRow}>
            <TouchableOpacity 
              style={styles.filterTitleButton}
              onPress={() => setShowMarcasOptions(!showMarcasOptions)}
            >
              <Text style={styles.filterCategoryTitle}>Marca</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.filterExpandButton}
              onPress={() => setShowMarcasOptions(!showMarcasOptions)}
            >
              <Chevron open={showMarcasOptions} />
            </TouchableOpacity>
          </View>

          {showMarcasOptions && (
            <View style={styles.filterOptionsContainer}>
              <TouchableOpacity
                style={[styles.categoryItem, selectedMarcas.length === 0 && styles.activeCategoryItem]}
                onPress={() => handleMarcaToggle('TODAS')}
              >
                <Text style={[styles.categoryText, selectedMarcas.length === 0 && styles.activeCategoryText]}>TODAS</Text>
              </TouchableOpacity>
              
              {marcas.length > 0 ? (
                marcas.map(marca => (
                  <TouchableOpacity
                    key={marca}
                    style={[styles.categoryItem, selectedMarcas.includes(marca) && styles.activeCategoryItem]}
                    onPress={() => handleMarcaToggle(marca)}
                  >
                    <Text style={[styles.categoryText, selectedMarcas.includes(marca) && styles.activeCategoryText]}>
                      {marca}
                    </Text>
                  </TouchableOpacity>
                ))
              ) : (
                <Text>No hay marcas disponibles</Text>
              )}
            </View>
          )}
        </View>

        {/* Contenido (Unidad de medida) */}
        <View style={styles.filterColumn}>
          <View style={styles.filterTitleRow}>
            <TouchableOpacity 
              style={styles.filterTitleButton}
              onPress={() => setShowContenidoOptions(!showContenidoOptions)}
            >
              <Text style={styles.filterCategoryTitle}>Contenido</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.filterExpandButton}
              onPress={() => setShowContenidoOptions(!showContenidoOptions)}
            >
              <Chevron open={showContenidoOptions} />
            </TouchableOpacity>
          </View>

          {showContenidoOptions && (
            <View style={styles.filterOptionsContainer}>
              <TouchableOpacity
                style={[styles.categoryItem, selectedUnidadesMedida.length === 0 && styles.activeCategoryItem]}
                onPress={() => handleUnidadMedidaToggle('TODAS')}
              >
                <Text style={[styles.categoryText, selectedUnidadesMedida.length === 0 && styles.activeCategoryText]}>TODOS</Text>
              </TouchableOpacity>
              
              {getUnidadesMedida().length > 0 ? (
                getUnidadesMedida().map(item => (
                  <TouchableOpacity
                    key={item.combinacion}
                    style={[styles.categoryItem, selectedUnidadesMedida.includes(item.combinacion) && styles.activeCategoryItem]}
                    onPress={() => handleUnidadMedidaToggle(item.combinacion)}
                  >
                    <Text style={[styles.categoryText, selectedUnidadesMedida.includes(item.combinacion) && styles.activeCategoryText]}>
                      {item.combinacion}
                    </Text>
                  </TouchableOpacity>
                ))
              ) : (
                <Text style={styles.noOptionsText}>No hay unidades disponibles</Text>
              )}
            </View>
          )}
        </View>

        {/* Rango de precio */}
        <View style={styles.filterPriceSection}>
          <Text style={styles.filterCategoryTitle}>Precio</Text>
          <View style={styles.priceRangeContainer}>
            {productos && productos.length > 0 ? (
              <View style={styles.priceFilterContainer}>
                <RangeSlider
                  minValue={tempPriceRange.min}
                  maxValue={tempPriceRange.max}
                  minLimit={preciosAnalizados.min}
                  maxLimit={preciosAnalizados.max}
                  onChange={(values) => {
                    // Verificar que los valores son válidos
                    if (Array.isArray(values) && values.length === 2 && 
                        !isNaN(values[0]) && !isNaN(values[1])) {
                      setTempPriceRange({
                        min: values[0],
                        max: values[1]
                      });
                    }
                  }}
                />
                
                <View style={styles.priceInputsContainer}>
                  <Text style={styles.priceValue}>${Math.round(tempPriceRange.min)}</Text>
                  <Text style={styles.priceSeparator}>-</Text>
                  <Text style={styles.priceValue}>${Math.round(tempPriceRange.max)}</Text>
                </View>
                
                {/* En pantallas grandes, mantener los botones en la sección de precio */}
                {!isSmallScreen && renderActionButtons()}
              </View>
            ) : (
              <Text style={styles.noOptionsText}>No hay productos disponibles</Text>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingLeft: Platform.OS === 'web' ? 10 : 10,
    paddingTop: Platform.OS === 'web' ? 10 : 10,

    backgroundColor: Platform.OS === 'web' ? 'transparent' : 'white',
    borderRadius: Platform.OS === 'web' ? 0 : 8,
    marginBottom: 16,
    marginTop: 0, // Quitamos el margen superior para evitar espacio innecesario
    position: 'relative',
    //zIndex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center'
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8
  },
  categoriesContainer: {
    marginBottom: 16
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8
  },
  checkboxSelected: {
    backgroundColor: colores.rojo,
    borderColor: colores.rojo
  },
  checkboxLabel: {
    flex: 1
  },
  priceFilterContainer: {
    marginTop: 16,
    alignItems: 'center'
  },
  priceInputsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
    width: '100%',
    justifyContent: 'center'
  },
  priceInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    width: 80,
    textAlign: 'center'
  },
  priceSeparator: {
    marginHorizontal: 8
  },
  priceValue: {
    fontSize: 16,
    fontWeight: 'bold'
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16
  },
  button: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center'
  },
  resetButton: {
    backgroundColor: '#f0f0f0'
  },
  applyButton: {
    backgroundColor: colores.rojo
  },
  buttonText: {
    fontWeight: 'bold'
  },
  resetButtonText: {
    color: '#333'
  },
  applyButtonText: {
    color: 'white'
  },
  filtersPanelTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    color: colores.rojo,
  },
  filtersPanelContainer: {
   // backgroundColor: 'white',
   // borderRadius: 10,
    //shadowColor: '#000',
    //shadowOffset: { width: 0, height: 2 },
    //shadowOpacity: 0.1,
    //shadowRadius: 4,
    //elevation: 3,
  },
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  filterColumn: {
    width: '100%',
    marginBottom: 10,
  },
  filterCategoryTitle: {
    fontSize: 16,
   fontWeight: 'bold',
    marginBottom: 5,
  },
  filterOptionsContainer: {
   // marginBottom: 20,
  },
  filterPriceSection: {
    width: '95%',
    marginTop: 10,
  },
  // Contenedor para los botones en línea
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
    gap: 10, // Espacio entre botones
  },
  buttonsContainerTop: {
    marginBottom: 10,
  },
  applyPriceButton: {
    backgroundColor: colores.rojo,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 15,
    alignItems: 'center',
  },
  applyPriceButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  priceRangeContainer: {
    width: '100%',
  },
  categoryItem: {
    paddingHorizontal: 0,
    //paddingVertical: 8,
    borderRadius: 0,
    marginBottom: 8,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  activeCategoryItem: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  categoryText: {
    fontSize: 14,
  },
  activeCategoryText: {
    color: colores.rojo,
    fontWeight: 'bold',
  },
  filterTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  filterTitleButton: {
    flex: 1,
    paddingVertical: 5,
  },
  filterExpandButton: {
  },
  expandButtonText: {
    fontSize: 20,
    color: colores.rojo,
  },
  chevronWrapper: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    width: 10,
    height: 10,
    borderLeftWidth: 2,
    borderBottomWidth: 2,
    borderColor: colores.rojo,
  },
  clearFiltersButton: {
    borderWidth: 1,
    borderColor: colores.rojo,
    borderRadius: 15,
    paddingVertical: 6,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  clearFiltersButtonText: {
    color: colores.rojo,
    fontWeight: 'bold',
  },
  noOptionsText: {
    fontSize: 14,
    fontStyle: 'italic',
    color: '#888',
    marginVertical: 10,
  },
  toggleFiltersButton: {
    backgroundColor: colores.rojo,
    paddingVertical: 10,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    //zIndex: 10,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }
      : {
          elevation: 2,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 2,
        }
    ),
    borderBottomWidth: 1,
    borderBottomColor: '#c41212',
  },
  toggleFiltersText: {
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: 14,
  },
});

export default Filtros;
